import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { solutions } from '@/data/solutions'
import { functions } from '@/data/functions'

/**
 * Desktop hover/click dropdown for the marketing nav's Solutions item.
 *
 * Lists every industry vertical from src/data/solutions.ts as a direct
 * link to /solutions/<slug>, plus a "View all solutions" footer that
 * goes to the index page. Keeps internal links to industry pages
 * surfaced from every marketing route's header — better SEO than the
 * flat Solutions link this replaces, and shorter clicks-to-vertical
 * for visitors who know which industry they're in.
 *
 * Opens on hover (with a small close delay so the mouse can travel to
 * the panel) and on click (for touch + keyboard). Closes on outside
 * click and Escape. Drop into the same spot a plain
 * <Link to="/solutions">Solutions</Link> used to live.
 */
export function SolutionsDropdown() {
  const [open, setOpen] = useState(false)
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)

  const openMenu = () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
    setOpen(true)
  }

  const scheduleClose = () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
    // 120ms grace period so the mouse can cross the gap between the
    // trigger and the panel without the menu collapsing under it.
    closeTimeoutRef.current = setTimeout(() => setOpen(false), 120)
  }

  // Close on outside click + Escape. Both belt and suspenders — the
  // outside-click handler covers mouse users, Escape covers keyboard.
  useEffect(() => {
    if (!open) return
    const onClickAway = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', onClickAway)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onClickAway)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={openMenu}
      onMouseLeave={scheduleClose}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
      >
        Solutions
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-1/2 top-full z-50 mt-2 w-[36rem] -translate-x-1/2 rounded-xl border border-gray-200 bg-white p-3 shadow-lg shadow-gray-200/50"
        >
          <div className="grid grid-cols-2 gap-3">
            {/* By Function — surfaced first because cross-industry intent
                is what most buyers come in with. */}
            <div>
              <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                By function
              </div>
              <ul className="space-y-0.5">
                {functions.map((fn) => (
                  <li key={fn.slug}>
                    <Link
                      to={`/solutions/function/${fn.slug}`}
                      role="menuitem"
                      onClick={() => setOpen(false)}
                      className="block rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                    >
                      {fn.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                By industry
              </div>
              <ul className="space-y-0.5">
                {solutions.map((s) => (
                  <li key={s.slug}>
                    <Link
                      to={`/solutions/industry/${s.slug}`}
                      role="menuitem"
                      onClick={() => setOpen(false)}
                      className="block rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                    >
                      {s.industry}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="my-2 h-px bg-gray-100" />
          <Link
            to="/solutions"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block rounded-md px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 transition-colors"
          >
            View all solutions →
          </Link>
        </div>
      )}
    </div>
  )
}

/**
 * Mobile variant — renders as an expandable section inside an already-
 * open mobile menu (Landing.tsx is the only page with a mobile menu
 * for now). Uses click-to-toggle since hover doesn't exist on touch.
 */
export function SolutionsMobileMenu({ onNavigate }: { onNavigate?: () => void }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="flex w-full items-center justify-between py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
      >
        Solutions
        <ChevronDown size={16} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
        <div className="ml-3 mt-1 space-y-3 border-l border-gray-200 pl-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 py-1">By function</p>
            <ul className="space-y-0.5">
              {functions.map((fn) => (
                <li key={fn.slug}>
                  <Link
                    to={`/solutions/function/${fn.slug}`}
                    onClick={onNavigate}
                    className="block py-1.5 text-sm text-gray-600 hover:text-gray-900"
                  >
                    {fn.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 py-1">By industry</p>
            <ul className="space-y-0.5">
              {solutions.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/solutions/industry/${s.slug}`}
                    onClick={onNavigate}
                    className="block py-1.5 text-sm text-gray-600 hover:text-gray-900"
                  >
                    {s.industry}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <Link
            to="/solutions"
            onClick={onNavigate}
            className="block py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            View all →
          </Link>
        </div>
      )}
    </div>
  )
}
